import { useEffect, useState } from 'react';
import { useSnapshot } from 'valtio';

import { state as navigationState } from '@cartwhl/common/stores/navigationStore';

/**
 * This hook checks if there is an active session by looking for a 'jobID' parameter in the URL.
 * It listens for URL changes and updates the state accordingly.
 *
 * Be aware that the jobID might be invalid, returning a 404, and this hook will still return true.
 *
 * It is meant to display UI elements before all the API roundtrips have completed.
 * This prevents UI flashing and layout shifts when the page loads.
 *
 * @returns {boolean} True if there is an active session, false otherwise.
 */
export const useHasActiveSession = (): boolean => {
  const [hasJobID, setHasJobID] = useState<boolean>(false);
  // Subscribe to navigationStore changes so this hook updates when navigate() is called
  const navigationSnap = useSnapshot(navigationState);

  useEffect(() => {
    const checkJobID = () => {
      if (typeof window === 'undefined') return;
      const searchParams = new URLSearchParams(window.location.search);
      setHasJobID(Boolean(searchParams.get('jobID')));
    };

    checkJobID();
    window.addEventListener('popstate', checkJobID);

    return () => {
      window.removeEventListener('popstate', checkJobID);
    };
  }, [navigationSnap.search, navigationSnap.params]); // Re-run when navigation state changes

  return hasJobID;
};

/**
 * This hook checks if there is an active pose session by looking for a 'projectID' parameter in the URL.
 * It listens for URL changes and updates the state accordingly.
 *
 * Be aware that the projectID might be invalid, returning a 404, and this hook will still return true.
 *
 * It is meant to display UI elements before all the API roundtrips have completed.
 * This prevents UI flashing and layout shifts when the page loads.
 *
 * @returns {boolean} True if there is an active pose session, false otherwise.
 */
export const useHasActivePoseSession = (): boolean => {
  const [hasProjectID, setHasProjectID] = useState<boolean>(false);
  // Subscribe to navigationStore changes so this hook updates when navigate() is called
  const navigationSnap = useSnapshot(navigationState);

  useEffect(() => {
    const checkProjectID = () => {
      if (typeof window === 'undefined') return;
      const searchParams = new URLSearchParams(window.location.search);
      setHasProjectID(Boolean(searchParams.get('projectID')));
    };

    checkProjectID();
    window.addEventListener('popstate', checkProjectID);

    return () => {
      window.removeEventListener('popstate', checkProjectID);
    };
  }, [navigationSnap.search, navigationSnap.params]); // Re-run when navigation state changes

  return hasProjectID;
};
